"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractFieldInfoNodeIdsForCalculationFormula = extractFieldInfoNodeIdsForCalculationFormula;
exports.evaluateFrontendCalculationFormula = evaluateFrontendCalculationFormula;
exports.evaluateCalculationFormulaComponentHelper = evaluateCalculationFormulaComponentHelper;
exports.extractFieldInfoNodeIdsForRenderConditions = extractFieldInfoNodeIdsForRenderConditions;
exports.evaluateFrontendRenderCondition = evaluateFrontendRenderCondition;
const constants_1 = require("../constants");
function extractFieldInfoNodeIdsForCalculationFormula(formula) {
    const result = [];
    function traverse(component) {
        var _a;
        if (component.componentType === constants_1.FormulaComponentType.FIELD &&
            component.infoNodeId) {
            result.push(component.infoNodeId);
        }
        for (const child of (_a = component.children) !== null && _a !== void 0 ? _a : []) {
            traverse(child);
        }
    }
    traverse(formula.rootComponent);
    return result;
}
function evaluateFrontendCalculationFormula(formula, relatedValues) {
    const rootComponent = formula.rootComponent;
    return evaluateCalculationFormulaComponentHelper(rootComponent, relatedValues);
}
function evaluateCalculationFormulaComponentHelper(component, relatedValues) {
    const sortedChildren = [...component.children].sort((a, b) => a.evaluationOrder - b.evaluationOrder);
    let evaluatedChildren = [];
    switch (component.componentType) {
        //leaf nodes
        case constants_1.FormulaComponentType.FIELD:
            const value = relatedValues[component.infoNodeId];
            return value ? value : undefined;
        case constants_1.FormulaComponentType.CONSTANT:
            return component.value;
        case constants_1.FormulaComponentType.OPTION:
            return component.selectedOptionNodeId;
        //has children
        case constants_1.FormulaComponentType.MATH:
            const operator = component.operator;
            evaluatedChildren = sortedChildren.map((c) => {
                return evaluateCalculationFormulaComponentHelper(c, relatedValues);
            });
            if (evaluatedChildren.some((v) => v === undefined || isNaN(Number(v)))) {
                return undefined;
            }
            const values = evaluatedChildren.map((v) => Number(v));
            let result;
            switch (operator) {
                case "+":
                    result = values.reduce((sum, val) => sum + val, 0);
                    break;
                case "-":
                    result = values.reduce((diff, val, index) => index === 0 ? val : diff - val);
                    break;
                case "*":
                    result = values.reduce((prod, val) => prod * val, 1);
                    break;
                case "/":
                    if (values.slice(1).some((v) => v === 0)) {
                        return undefined;
                    }
                    result = values.reduce((quot, val, index) => index === 0 ? val : quot / val);
                    break;
            }
            return result;
        case constants_1.FormulaComponentType.COMPARE:
            const compareOperator = component.operator;
            evaluatedChildren = sortedChildren.map((c) => {
                return evaluateCalculationFormulaComponentHelper(c, relatedValues);
            });
            switch (compareOperator) {
                case constants_1.ConditionOperand.EQ:
                    return evaluatedChildren.every((v) => v == evaluatedChildren[0]);
                case constants_1.ConditionOperand.NE:
                    return !evaluatedChildren.some((v) => v == evaluatedChildren[0]);
                case constants_1.ConditionOperand.GT:
                    for (let i = 0; i < evaluatedChildren.length - 1; i++) {
                        if (evaluatedChildren[i] === undefined ||
                            evaluatedChildren[i + 1] === undefined ||
                            isNaN(Number(evaluatedChildren[i])) ||
                            isNaN(Number(evaluatedChildren[i + 1])))
                            return undefined;
                        if (!(Number(evaluatedChildren[i]) > Number(evaluatedChildren[i + 1])))
                            return false;
                    }
                    return true;
                case constants_1.ConditionOperand.LT:
                    for (let i = 0; i < evaluatedChildren.length - 1; i++) {
                        if (evaluatedChildren[i] === undefined ||
                            evaluatedChildren[i + 1] === undefined ||
                            isNaN(Number(evaluatedChildren[i])) ||
                            isNaN(Number(evaluatedChildren[i + 1])))
                            return undefined;
                        if (!(Number(evaluatedChildren[i]) < Number(evaluatedChildren[i + 1])))
                            return false;
                    }
                    return true;
                case constants_1.ConditionOperand.GTE:
                    for (let i = 0; i < evaluatedChildren.length - 1; i++) {
                        if (evaluatedChildren[i] === undefined ||
                            evaluatedChildren[i + 1] === undefined ||
                            isNaN(Number(evaluatedChildren[i])) ||
                            isNaN(Number(evaluatedChildren[i + 1])))
                            return undefined;
                        if (!(Number(evaluatedChildren[i]) >= Number(evaluatedChildren[i + 1])))
                            return false;
                    }
                    return true;
                case constants_1.ConditionOperand.LTE:
                    for (let i = 0; i < evaluatedChildren.length - 1; i++) {
                        if (evaluatedChildren[i] === undefined ||
                            evaluatedChildren[i + 1] === undefined ||
                            isNaN(Number(evaluatedChildren[i])) ||
                            isNaN(Number(evaluatedChildren[i + 1])))
                            return undefined;
                        if (!(Number(evaluatedChildren[i]) <= Number(evaluatedChildren[i + 1])))
                            return false;
                    }
                    return true;
                case constants_1.ConditionOperand.EXISTS:
                    return (evaluatedChildren[0] !== undefined && evaluatedChildren[0] !== null);
                case constants_1.ConditionOperand.NOT_EXIST:
                    return (evaluatedChildren[0] === undefined || evaluatedChildren[0] === null);
            }
        case constants_1.FormulaComponentType.LOGIC:
            const logicOperand = component.operator;
            switch (logicOperand) {
                case constants_1.LogicOperand.AND: {
                    for (const child of sortedChildren) {
                        const value = evaluateCalculationFormulaComponentHelper(child, relatedValues);
                        if (value === undefined)
                            return undefined; // propagate undefined
                        if (!Boolean(value))
                            return false; // false stops evaluation immediately
                    }
                    return true; // all truthy
                }
                case constants_1.LogicOperand.OR: {
                    let sawUndefined = false;
                    for (const child of sortedChildren) {
                        const value = evaluateCalculationFormulaComponentHelper(child, relatedValues);
                        if (value === undefined) {
                            sawUndefined = true;
                            continue; // must continue because still possible for later children to be true
                        }
                        if (Boolean(value))
                            return true; // true stops early
                    }
                    // No true found
                    return sawUndefined ? undefined : false;
                }
                case constants_1.LogicOperand.NOT: {
                    // Only the first child matters
                    if (sortedChildren.length === 0)
                        return undefined;
                    const value = evaluateCalculationFormulaComponentHelper(sortedChildren[0], relatedValues);
                    return value === undefined ? undefined : !Boolean(value);
                }
            }
        case constants_1.FormulaComponentType.FUNCTION:
            const functionOperand = component.operator;
            evaluatedChildren = sortedChildren.map((c) => {
                return evaluateCalculationFormulaComponentHelper(c, relatedValues);
            });
            switch (functionOperand) {
                case constants_1.FunctionOperand.MAX:
                    if (evaluatedChildren.some((v) => v === undefined || isNaN(Number(v)))) {
                        return undefined;
                    }
                    return Math.max(...evaluatedChildren.map((v) => Number(v)));
                case constants_1.FunctionOperand.AVG:
                    if (evaluatedChildren.some((v) => v === undefined || isNaN(Number(v)))) {
                        return undefined;
                    }
                    return (evaluatedChildren
                        .map((v) => {
                        return Number(v);
                    })
                        .reduce((sum, v) => sum + Number(v), 0) / evaluatedChildren.length);
                case constants_1.FunctionOperand.AVG_IF_VALID_POSITIVE:
                    const filtered = evaluatedChildren
                        .filter((v) => {
                        return v !== undefined && !isNaN(Number(v)) && Number(v) > 0;
                    })
                        .map((v) => {
                        return Number(v);
                    });
                    return filtered.length
                        ? filtered.reduce((sum, v) => sum + Number(v), 0) / filtered.length
                        : 0;
                case constants_1.FunctionOperand.REPLACE_IF_LESS_THAN:
                    // children: [value, condition_value, replacement]
                    const value = evaluatedChildren[0];
                    const conditionalValue = evaluatedChildren[1];
                    const replacement = evaluatedChildren[2];
                    if (value === undefined ||
                        conditionalValue === undefined ||
                        isNaN(Number(value)) ||
                        isNaN(Number(conditionalValue))) {
                        return undefined;
                    }
                    return Number(value) < Number(conditionalValue)
                        ? replacement
                        : Number(value);
            }
        case constants_1.FormulaComponentType.IFELSE:
            const IfElseOperands = component.operator;
            switch (IfElseOperands) {
                case "IFBLOCK":
                    const childsSortedChildren = [...component.children].sort((a, b) => a.evaluationOrder - b.evaluationOrder);
                    let i = 0;
                    while (i < childsSortedChildren.length) {
                        const current = childsSortedChildren[i];
                        const op = current.operator;
                        if (op === "IF" || op === "ELSE IF") {
                            const condition = evaluateCalculationFormulaComponentHelper(current, relatedValues);
                            if (condition === undefined)
                                return undefined;
                            const thenChild = childsSortedChildren[i + 1];
                            if (!thenChild || thenChild.operator !== "THEN")
                                return undefined;
                            if (Boolean(condition)) {
                                return evaluateCalculationFormulaComponentHelper(thenChild, relatedValues);
                            }
                            i += 2;
                        }
                        else if (op === "ELSE") {
                            return evaluateCalculationFormulaComponentHelper(current, relatedValues);
                        }
                        else {
                            return undefined; // invalid operator here
                        }
                    }
                    return undefined; // no conditions matched and no ELSE given
                case "IF":
                case "THEN":
                case "ELSE IF":
                case "ELSE":
                    const value = evaluateCalculationFormulaComponentHelper(sortedChildren[0], relatedValues);
                    return value;
            }
    }
}
function extractFieldInfoNodeIdsForRenderConditions(condition) {
    const result = [];
    function traverse(condition) {
        var _a;
        if (condition.infoNodeId) {
            result.push(condition.infoNodeId);
        }
        for (const child of (_a = condition.children) !== null && _a !== void 0 ? _a : []) {
            traverse(child);
        }
    }
    traverse(condition);
    return result;
}
function evaluateFrontendRenderCondition(renderCondition, relatedValues) {
    const sortedChildren = [...renderCondition.children].sort((a, b) => a.evaluationOrder - b.evaluationOrder);
    switch (renderCondition.nodeType) {
        case constants_1.ConditionNodeType.ONE_LINE_CONDITION:
            const lhsValue = relatedValues[renderCondition.infoNodeId];
            const rhsValue = renderCondition.value || renderCondition.selectedOptionNodeId;
            switch (renderCondition.condition) {
                case constants_1.ConditionOperand.EQ:
                    return lhsValue == rhsValue;
                case constants_1.ConditionOperand.NE:
                    return lhsValue != rhsValue;
                case constants_1.ConditionOperand.GT:
                    return Number(lhsValue) > Number(rhsValue);
                case constants_1.ConditionOperand.LT:
                    return Number(lhsValue) < Number(rhsValue);
                case constants_1.ConditionOperand.GTE:
                    return Number(lhsValue) >= Number(rhsValue);
                case constants_1.ConditionOperand.LTE:
                    return Number(lhsValue) <= Number(rhsValue);
                case constants_1.ConditionOperand.EXISTS:
                    return lhsValue != undefined && lhsValue != null;
                case constants_1.ConditionOperand.NOT_EXIST:
                    return lhsValue == undefined || lhsValue == null;
            }
            break;
        case constants_1.ConditionNodeType.OPERAND:
            switch (renderCondition.logicOperand) {
                case constants_1.LogicOperand.AND: {
                    for (const child of sortedChildren) {
                        const value = evaluateFrontendRenderCondition(child, relatedValues);
                        if (value === undefined)
                            return undefined; // propagate undefined
                        if (!Boolean(value))
                            return false; // false stops evaluation immediately
                    }
                    return true; // all truthy
                }
                case constants_1.LogicOperand.OR: {
                    let sawUndefined = false;
                    for (const child of sortedChildren) {
                        const value = evaluateFrontendRenderCondition(child, relatedValues);
                        if (value === undefined) {
                            sawUndefined = true;
                            continue; // must continue because still possible for later children to be true
                        }
                        if (Boolean(value))
                            return true; // true stops early
                    }
                    // No true found
                    return sawUndefined ? undefined : false;
                }
                case constants_1.LogicOperand.NOT: {
                    // Only the first child matters
                    if (sortedChildren.length === 0)
                        return undefined;
                    const value = evaluateFrontendRenderCondition(sortedChildren[0], relatedValues);
                    return value === undefined ? undefined : !Boolean(value);
                }
            }
    }
}
